import { CSSProperties, MouseEvent } from 'react';

import { saloonFonts, saloonTokens } from './saloonTheme';

/**
 * Saloon CTA button — leather-on-ink with an orange edge and Rye lettering.
 * This is the canonical "Join Table" treatment (formerly `joinBtnStyle` in
 * JoinButton.tsx). Shared so every join/waitlist CTA reads identically:
 * Join Table, Join Private Table, Rejoin, Accept Seat, Join Waitlist, Set Buy-In.
 *
 * Box-shadow is static; hover only scales (transform) and must be gated by
 * `useSupportsHover` — see `saloonHoverScaleIn` / `saloonHoverScaleOut`.
 */
export const saloonButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  padding: '0.85rem 2.4rem',
  background: 'linear-gradient(180deg, rgba(74,35,10,0.6), rgba(40,18,6,0.7))',
  border: '1.5px solid #e8821f',
  borderRadius: '8px',
  color: '#ffb45c',
  fontFamily: saloonFonts.decorative, // 'Rye', serif
  fontWeight: 400,
  fontSize: '1rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  minHeight: '52px',
  maxWidth: '320px',
  boxShadow: '0 4px 14px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,180,92,0.15)',
  transition: 'transform 0.12s ease'
};

/**
 * Grayed saloon variant for the secondary "Exit Waitlist" CTA — Unity applies
 * `ColourUtility.ColorGray` to this button (TablesView.cs:2247). Same leather
 * idiom so it stays in the saloon family, just desaturated to read as muted.
 */
export const saloonMutedButtonStyle: CSSProperties = {
  ...saloonButtonStyle,
  background: 'linear-gradient(180deg, rgba(48,48,48,0.6), rgba(26,26,26,0.7))',
  border: '1.5px solid rgba(180,180,180,0.5)',
  color: 'rgba(220,220,220,0.85)',
  boxShadow: '0 4px 14px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)'
};

/** Applied on top of a saloon button when the action is unavailable. */
export const saloonButtonDisabledStyle: CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed'
};

/**
 * Small square icon-only saloon button — the canonical "hamburger" chrome
 * (formerly `saloonIconBtn` duplicated per-callsite in TableHeader.tsx).
 * Shared so every icon-trigger button (menu, support, guest table menu) reads
 * identically. Pair with `saloonHoverScaleIn`/`saloonHoverScaleOut`.
 */
export const saloonIconBtnStyle: CSSProperties = {
  position: 'relative',
  width: 32,
  height: 32,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '5px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(232,130,31,0.32)',
  color: saloonTokens.bone,
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'transform 0.12s ease',
  pointerEvents: 'auto'
};

/**
 * Scale-only hover handlers. Spread these onto a saloon button **only** when
 * `useSupportsHover()` is true, so touch devices keep their first-tap click
 * (iOS Safari suppresses the click that follows an emulated `mouseenter`).
 */
export const saloonHoverScaleIn = (e: MouseEvent<HTMLElement>) => {
  e.currentTarget.style.transform = 'scale(1.04)';
};

export const saloonHoverScaleOut = (e: MouseEvent<HTMLElement>) => {
  e.currentTarget.style.transform = 'scale(1)';
};
